/* PMT USP marquee — seamless auto-scrolling ticker.
   Progressive enhancement: without JS (or with reduced motion) the list is a
   plain horizontally scrollable row. With JS the group is cloned (aria-hidden)
   until the track covers >= 2x the viewport, then a CSS animation translates
   the track by exactly one group width for a seamless loop at a constant px/s
   speed regardless of content length. A visible pause/play button satisfies
   WCAG 2.2.2 for touch and keyboard users. */
(function () {
  'use strict';

  if (customElements.get('pmt-marquee')) return;

  class PmtMarquee extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.pmt-marquee__viewport');
      this.track = this.querySelector('.pmt-marquee__track');
      this.group = this.querySelector('.pmt-marquee__group');
      this.toggle = this.querySelector('.pmt-marquee__toggle');
      if (!this.viewport || !this.track || !this.group) return;

      this.speed = Math.max(parseFloat(this.dataset.speed) || 60, 1);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = this.update.bind(this);

      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onMotionChange);
      }

      /* Watch the group as well as the host: the group's width changes when
         the web font swaps in, which the host's fixed-height box never
         reflects — without this the loop distance goes stale and the seam
         visibly jumps on cold-cache visits. */
      this.resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.update());
      });
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.update());
      }

      /* Pause the animation while offscreen — no wasted main-thread work. */
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.toggleAttribute('data-offscreen', !entry.isIntersecting);
        });
      });
      this.intersectionObserver.observe(this);

      if (this.toggle) {
        this.onToggle = () => {
          const paused = this.toggleAttribute('data-user-paused');
          this.toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        };
        this.toggle.addEventListener('click', this.onToggle);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.reducedMotion && this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.onMotionChange);
      }
      if (this.toggle && this.onToggle) {
        this.toggle.removeEventListener('click', this.onToggle);
      }
    }

    update() {
      if (
        this.dataset.enabled !== 'true' ||
        this.reducedMotion.matches ||
        this.group.children.length === 0
      ) {
        this.stop();
        return;
      }

      const groupWidth = this.group.getBoundingClientRect().width;
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      if (groupWidth < 1 || viewportWidth < 1) {
        this.stop();
        return;
      }

      /* Enough copies that the track always covers the viewport while one
         full group scrolls past: total >= viewport + one group. */
      const copiesNeeded = Math.max(Math.ceil(viewportWidth / groupWidth) + 1, 2);
      const currentCopies = this.track.querySelectorAll('.pmt-marquee__group').length;

      for (let i = currentCopies; i < copiesNeeded; i += 1) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.track.appendChild(clone);
      }

      this.style.setProperty('--pmt-marquee-loop', groupWidth + 'px');
      this.style.setProperty('--pmt-marquee-duration', (groupWidth / this.speed).toFixed(3) + 's');
      this.setAttribute('data-animate', '');
      if (this.toggle) this.toggle.hidden = false;
    }

    stop() {
      this.removeAttribute('data-animate');
      this.track.querySelectorAll('.pmt-marquee__group[aria-hidden="true"]').forEach((clone) => {
        clone.remove();
      });
      if (this.toggle) this.toggle.hidden = true;
    }
  }

  customElements.define('pmt-marquee', PmtMarquee);
})();
